// Shared visual primitives (Icon, IconBtn, Chip, Tag, BigButton, Patch,
// SectionTitle, TabBar, ImageModal…). Inline-styled against theme.PALETTE —
// there is no CSS framework; keep new primitives here rather than one-off
// styles in screens.
import React from 'react';
import { PALETTE, headerPad } from '../theme';

// ── Icons ─────────────────────────────────────────────────────────────────────
export function Icon({ name, size = 18, stroke = 1.5, style }) {
  const p = (d, extra) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={style}>
      {typeof d === 'string' ? <path d={d} /> : d}
      {extra}
    </svg>
  );
  switch (name) {
    case 'plus':    return p('M12 5v14M5 12h14');
    case 'minus':   return p('M5 12h14');
    case 'arrow':   return p('M5 12h14M13 6l6 6-6 6');
    case 'arrowL':  return p('M19 12H5M11 6l-6 6 6 6');
    case 'arrowUp': return p('M12 19V5M6 11l6-6 6 6');
    case 'arrowDn': return p('M12 5v14M6 13l6 6 6-6');
    case 'check':   return p('M4 12l5 5L20 6');
    case 'x':       return p('M6 6l12 12M18 6L6 18');
    case 'cal':     return p(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>);
    case 'pin':     return p(<><path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" /><circle cx="12" cy="9" r="2.5" /></>);
    case 'sparkle': return p('M12 3v6m0 6v6m-9-9h6m6 0h6M6 6l3 3m6 6l3 3M18 6l-3 3M6 18l3-3');
    case 'search':  return p(<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>);
    case 'menu':    return p('M4 7h16M4 12h16M4 17h16');
    case 'more':    return p(<><circle cx="6" cy="12" r="1.2" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/><circle cx="18" cy="12" r="1.2" fill="currentColor" stroke="none"/></>);
    case 'star':    return p('M12 4l2.6 5.3 5.9.9-4.3 4.1 1 5.9L12 17.6 6.8 20.2l1-5.9L3.5 10.2l5.9-.9L12 4z');
    case 'heart':   return p('M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z');
    case 'bag':     return p(<><path d="M5 8h14l-1 12H6L5 8z" /><path d="M9 8V6a3 3 0 0 1 6 0v2" /></>);
    case 'plane':   return p('M3 11l18-7-7 18-2-8-9-3z');
    case 'bed':     return p(<><path d="M3 17V8M21 17V12a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3" /><path d="M3 14h18M3 19h.01M21 19h.01" /></>);
    case 'car':     return p(<><path d="M5 16V11l2-5h10l2 5v5" /><path d="M3 16h18v3a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3z" /><circle cx="8" cy="16" r="1" fill="currentColor" stroke="none"/><circle cx="16" cy="16" r="1" fill="currentColor" stroke="none"/></>);
    case 'ticket':  return p('M4 9V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z');
    case 'home':    return p('M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1v-9z');
    case 'compass': return p(<><circle cx="12" cy="12" r="9" /><path d="M15 9l-2 5-5 2 2-5 5-2z" /></>);
    case 'globe':   return p(<><circle cx="12" cy="12" r="9" /><ellipse cx="12" cy="12" rx="4" ry="9" /><path d="M3 12h18" /></>);
    case 'list':    return p('M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01');
    case 'clock':   return p(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>);
    case 'spark':   return p(<><path d="M12 3v5M12 16v5M3 12h5M16 12h5" /></>);
    case 'user':    return p(<><circle cx="12" cy="8" r="4" /><path d="M4 20v-2a8 8 0 0 1 16 0v2" /></>);
    case 'pencil':  return p('M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.5 2.5 0 0 1 3.5 3.5L12 15l-4 1 1-4 9.5-9.5z');
    case 'shield':  return p('M12 3l8 4v5c0 4.4-3.4 8.5-8 10-4.6-1.5-8-5.6-8-10V7l8-4z');
    case 'wifi':    return p('M5 12.6A10 10 0 0 1 19 12.6M2 9.4a14 14 0 0 1 20 0M8.5 15.8a5 5 0 0 1 7 0M12 19h.01');
    case 'camera':  return p(<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>);
    case 'grid':    return p(<><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></>);
    case 'share':   return p(<><circle cx="18" cy="5" r="2.5" fill="currentColor" stroke="none" /><circle cx="6" cy="12" r="2.5" fill="currentColor" stroke="none" /><circle cx="18" cy="19" r="2.5" fill="currentColor" stroke="none" /><path d="M8.4 10.6l7.2-4.2M8.4 13.4l7.2 4.2" /></>);
    case 'settings': return p(<><circle cx="12" cy="12" r="3" /><path d="M19.4 13a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V19a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H4a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H10a1.7 1.7 0 0 0 1-1.5V4a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V10a1.7 1.7 0 0 0 1.5 1H20a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>);
    case 'bell':    return p(<><path d="M6 8a6 6 0 0 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6Z" /><path d="M10 20a2 2 0 0 0 4 0" /></>);
    case 'circleCheck':  return p(<><circle cx="12" cy="12" r="9" /><path d="M7.5 12.5l3 3 6-6.5" /></>);
    case 'circleDashed': return p(<circle cx="12" cy="12" r="9" strokeDasharray="3.2 3.2" />);
    default: return p('M12 12h.01');
  }
}

// ── Chip ──────────────────────────────────────────────────────────────────────
export function Chip({ children, active, onClick, style }) {
  return (
    <button type="button" onClick={onClick} style={{
      appearance: 'none', cursor: 'pointer',
      padding: '7px 14px', borderRadius: 999,
      fontFamily: 'Inter, system-ui', fontSize: 13, fontWeight: 500,
      letterSpacing: 0.1, whiteSpace: 'nowrap',
      background: active ? PALETTE.ink : 'transparent',
      color: active ? PALETTE.paper : PALETTE.inkSoft,
      border: active ? `1px solid ${PALETTE.ink}` : `1px solid ${PALETTE.rule}`,
      transition: 'all 0.15s',
      ...style,
    }}>{children}</button>
  );
}

// ── Tag ───────────────────────────────────────────────────────────────────────
export function Tag({ children, tone = 'default', style }) {
  const tones = {
    default: { bg: PALETTE.paperDim, fg: PALETTE.inkSoft },
    accent:  { bg: PALETTE.accent,   fg: '#fff' },
    red:     { bg: PALETTE.redSoft,  fg: PALETTE.red },
    ghost:   { bg: 'transparent',    fg: PALETTE.inkMute },
  };
  const t = tones[tone] || tones.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 4,
      fontFamily: 'JetBrains Mono, ui-monospace, monospace',
      fontSize: 10, fontWeight: 500,
      letterSpacing: 0.5, textTransform: 'uppercase',
      background: t.bg, color: t.fg, ...style,
    }}>{children}</span>
  );
}

// ── BigButton ─────────────────────────────────────────────────────────────────
export function BigButton({ children, onClick, style, disabled, secondary }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      appearance: 'none', cursor: disabled ? 'default' : 'pointer',
      padding: '16px 20px', borderRadius: 14, width: '100%',
      fontFamily: 'Inter, system-ui', fontSize: 16, fontWeight: 600,
      letterSpacing: 0.2,
      background: secondary ? 'transparent' : PALETTE.ink,
      color: secondary ? PALETTE.ink : PALETTE.paper,
      border: secondary ? `1px solid ${PALETTE.rule}` : 'none',
      opacity: disabled ? 0.4 : 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      transition: 'transform 0.15s, opacity 0.15s',
      ...style,
    }}>{children}</button>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────
export function Header({ left, right, title, subtitle, sticky = true, isMobile = true }) {
  return (
    <div style={{
      position: sticky ? 'sticky' : 'static', top: 0, zIndex: 4,
      background: PALETTE.paper,
      padding: headerPad(isMobile), display: 'flex', flexDirection: 'column', gap: 4,
      borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        minHeight: 36,
      }}>
        <div style={{ width: 36, display: 'flex' }}>{left}</div>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
          color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>{subtitle}</div>
        <div style={{ width: 36, display: 'flex', justifyContent: 'flex-end' }}>{right}</div>
      </div>
      {title && (
        <div style={{
          fontFamily: 'Fraunces, Georgia, serif', fontSize: 32, fontWeight: 400,
          letterSpacing: -0.8, color: PALETTE.ink, lineHeight: 1.05,
          padding: '4px 0 0',
        }}>{title}</div>
      )}
    </div>
  );
}

// ── IconBtn ───────────────────────────────────────────────────────────────────
export function IconBtn({ name, onClick, style, size = 20, label }) {
  return (
    <button type="button" onClick={onClick} aria-label={label || name} style={{
      appearance: 'none', border: 0, background: 'transparent',
      width: 36, height: 36, borderRadius: 999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: PALETTE.ink, cursor: 'pointer', padding: 0,
      ...style,
    }}>
      <Icon name={name} size={size} />
    </button>
  );
}

// ── SectionTitle ──────────────────────────────────────────────────────────────
export function SectionTitle({ children, kicker, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      padding: '0 20px', marginBottom: 12,
    }}>
      <div>
        {kicker && (
          <div style={{
            fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
            color: PALETTE.inkMute, letterSpacing: 1.5, textTransform: 'uppercase',
            marginBottom: 4,
          }}>{kicker}</div>
        )}
        <div style={{
          fontFamily: 'Fraunces, Georgia, serif', fontSize: 22, fontWeight: 400,
          letterSpacing: -0.4, color: PALETTE.ink,
        }}>{children}</div>
      </div>
      {action && (
        <button style={{
          appearance: 'none', border: 0, background: 'transparent',
          fontFamily: 'Inter, system-ui', fontSize: 13, fontWeight: 500,
          color: PALETTE.inkSoft, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 2,
        }}>{action}<Icon name="arrow" size={14} /></button>
      )}
    </div>
  );
}

// ── TabBar ────────────────────────────────────────────────────────────────────
export function TabBar({ active, onChange }) {
  const tabs = [
    { id: 'home',    label: 'Trips',       icon: 'home' },
    { id: 'discover',label: 'Discover',    icon: 'compass' },
    { id: 'map',     label: 'World Map',   icon: 'globe' },
    { id: 'bucket',  label: 'Inspiration', icon: 'star' },
    { id: 'profile', label: 'You',         icon: 'user' },
  ];
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
      width: '100%', maxWidth: 430,
      paddingBottom: 'max(12px, env(safe-area-inset-bottom))', paddingTop: 8, paddingLeft: 12, paddingRight: 12,
      background: 'rgba(244, 239, 230, 0.92)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      borderTop: `0.5px solid ${PALETTE.rule}`,
      display: 'flex', justifyContent: 'space-around',
      zIndex: 10,
    }}>
      {tabs.map(t => (
        <button key={t.id} type="button" onClick={() => onChange?.(t.id)} aria-current={active === t.id ? 'page' : undefined} style={{
          appearance: 'none', border: 0, background: 'transparent',
          padding: '6px 10px', cursor: 'pointer',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
          color: active === t.id ? PALETTE.ink : PALETTE.inkMute,
        }}>
          <Icon name={t.icon} size={22} stroke={active === t.id ? 2 : 1.5} />
          <span style={{
            fontFamily: 'Inter, system-ui', fontSize: 10, fontWeight: 500,
            letterSpacing: 0.3,
          }}>{t.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── Patch (gradient swatch) ───────────────────────────────────────────────────
export function Patch({ swatch, height = 120, label, badge, style, children }) {
  return (
    <div aria-hidden="true" style={{
      height, borderRadius: 10, position: 'relative', overflow: 'hidden',
      background: swatch || PALETTE.paperDim,
      ...style,
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 9px)',
      }} />
      {label && (
        <div style={{
          position: 'absolute', left: 10, bottom: 8,
          fontFamily: 'JetBrains Mono, monospace', fontSize: 9,
          color: 'rgba(255,255,255,0.85)', letterSpacing: 1, textTransform: 'uppercase',
        }}>{label}</div>
      )}
      {badge && (
        <div style={{
          position: 'absolute', top: 10, left: 10,
          padding: '3px 8px', borderRadius: 4,
          background: 'rgba(255,255,255,0.92)',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 9,
          color: PALETTE.ink, letterSpacing: 0.8, textTransform: 'uppercase',
          fontWeight: 600,
        }}>{badge}</div>
      )}
      {children}
    </div>
  );
}

// ── ImageModal ────────────────────────────────────────────────────────────────
export function ImageModal({ src, onClose }) {
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <img src={src} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }} />
      <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, appearance: 'none', border: 0, background: 'rgba(255,255,255,0.15)', color: '#fff', width: 36, height: 36, borderRadius: 999, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
    </div>
  );
}
